import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Collapse,
  useTheme,
} from "@mui/material";
import {
  Search,
  EditNote,
  AccountTree,
  Visibility,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  Email,
} from "@mui/icons-material";

import Header from "../../global_components/parameterized/Header";

// ── SectionTitle ──────────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <Typography
    variant="h4"
    gutterBottom
    sx={{ fontWeight: "bold", color: "#11192A", mb: 3, mt: 1 }}
  >
    {children}
  </Typography>
);

// ── Value card ────────────────────────────────────────────────────────────────
const ValueCard = ({ icon: Icon, title, body }) => (
  <Card
    sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      boxShadow: 3,
    }}
  >
    <CardContent sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Icon sx={{ fontSize: 40, color: "#11192A", mr: 1.5 }} />
        <Typography variant="h4" fontWeight="bold" color="#11192A">
          {title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Typography
        variant="body1"
        color="text.primary"
        sx={{ fontSize: "1rem", lineHeight: 1.8 }}
      >
        {body}
      </Typography>
    </CardContent>
  </Card>
);

// ── FAQ item ──────────────────────────────────────────────────────────────────
const FaqItem = ({ num, question, children }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  return (
    <Card
      sx={{
        mb: 2,
        boxShadow: open ? 3 : 1,
        border: "1px solid",
        borderColor: open ? theme.palette.primary.main : "divider",
        transition: "all 0.2s",
      }}
    >
      <CardContent
        onClick={() => setOpen((v) => !v)}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2.5,
          px: 3,
          "&:last-child": { pb: 2.5 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Chip
            label={num}
            size="medium"
            sx={{
              fontWeight: "bold",
              minWidth: 52,
              fontSize: "0.95rem",
              backgroundColor: "#e3f2fd",
              color: "#1565c0",
            }}
          />
          <Typography variant="h4" fontWeight="bold" color="#11192A">
            {question}
          </Typography>
        </Box>
        {open ? (
          <ExpandLess
            sx={{ color: "primary.main", flexShrink: 0, fontSize: 32 }}
          />
        ) : (
          <ExpandMore
            sx={{ color: "text.secondary", flexShrink: 0, fontSize: 32 }}
          />
        )}
      </CardContent>
      <Collapse in={open}>
        <Divider />
        <CardContent sx={{ pt: 3, px: 4, pb: 3 }}>{children}</CardContent>
      </Collapse>
    </Card>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const LandingPage = () => {
  const valueCards = [
    {
      icon: Search,
      title: "Preview Before Download",
      body: "Field experiments are time-, resource-, and personnel-intensive. The FDP increases the visibility, comparability, and reusability of datasets by enabling structured exploration and early evaluation before any raw data download.",
    },
    {
      icon: EditNote,
      title: "FAIR Data by Design",
      body: "Datasets are published as structured research assets, not monolithic archives. They become Findable, Accessible, Interoperable, and Reusable without requiring manual curation efforts per request.",
    },
    {
      icon: Visibility,
      title: "Contextual Representation",
      body: "Experiments are described with rich contextual metadata such as environmental conditions and sensor configurations, allowing datasets to be understood and compared without inspecting raw files.",
    },
    {
      icon: AccountTree,
      title: "Towards Data Mesh",
      body: "The platform is evolving toward Data Mesh principles, where research domains manage their own datasets as structured data products while sharing them across the engineering research community.",
    },
  ];

  const bulletPoints = [
    {
      label: "Your name & institution",
      detail: "So we know who you are and can reach you back.",
    },
    {
      label: "A short experiment description",
      detail: "What kind of field experiments are you conducting?",
    },
    {
      label: "Sensor types & data formats",
      detail: "e.g. Camera, IMU, GPS, LiDAR, and any relevant formats.",
    },
    {
      label: "Your research domain",
      detail: "e.g. Automotive, Robotics, Agriculture, Environmental Science.",
    },
  ];

  return (
    <Box m="20px">
      <Header
        title="NFDI4ING’s Field Database Platform"
        subtitle="Understand, explore, and compare field experiments before accessing raw datasets."
      />

      {/* ── 1. Intro ── */}
      <Card
        sx={{
          mb: 5,
          boxShadow: 3,
          background: "#11192A",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(25,118,210,0.20) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <CardContent sx={{ p: 5 }}>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{ color: "#fff", mb: 3 }}
          >
            What is the Field Database Platform?
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{
              color: "rgba(255,255,255,0.92)",
              lineHeight: 1.9,
              fontSize: "1.1rem",
            }}
          >
            <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>
              NFDI4ING’s Field Database Platform
            </Box>{" "}
            is a service for researchers working with field data across the
            engineering sciences, including universities, universities of
            applied sciences, and non-university research institutions. The
            platform serves as a{" "}
            <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>
              discovery and management layer
            </Box>{" "}
            for{" "}
            <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>
              sensor-based field experiment data
            </Box>
            . Instead of acting as a traditional file archive, the FDP sits{" "}
            <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>
              between data collection and long-term storage
            </Box>
            , enabling researchers to publish{" "}
            <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>
              structured descriptions of experiments and datasets
            </Box>{" "}
            so others can explore, filter, and evaluate experiments before
            accessing large volumes of raw sensor data. Experiments are
            represented through{" "}
            <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>
              contextual metadata
            </Box>{" "}
            such as environmental conditions, sensor configurations, and
            experiment parameters. This makes datasets understandable and
            comparable without opening raw files. The platform is currently
            evolving toward a{" "}
            <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>
              Data Mesh–architecture
            </Box>
            , supporting{" "}
            <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>
              domain ownership of datasets
            </Box>
            ,{" "}
            <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>
              data-as-product principles
            </Box>
            , and{" "}
            <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>
              federated governance
            </Box>{" "}
            across research domains.
          </Typography>
        </CardContent>
      </Card>

      {/* ── 2. Added Value ── */}
      <SectionTitle>Added Value</SectionTitle>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {valueCards.map((card, i) => (
          <Grid item xs={12} lg={6} key={i}>
            <ValueCard {...card} />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 5 }} />

      {/* ── 3. FAQ ── */}
      <SectionTitle>Frequently Asked Questions</SectionTitle>
      <Box sx={{ mb: 2 }}>
        <FaqItem num="01" question="What problem does the platform solve?">
          <Typography
            variant="body1"
            paragraph
            color="text.primary"
            sx={{ fontSize: "1.05rem", lineHeight: 1.8 }}
          >
            Field experiments generate valuable datasets, but they are often
            difficult to understand or reuse because important contextual
            information is missing. The platform solves this by publishing
            structured descriptions of experiments, allowing researchers to
            explore datasets and understand their context before accessing the
            raw data.
          </Typography>
        </FaqItem>

        <FaqItem num="02" question="Who is this platform for?">
          <Typography
            variant="body1"
            paragraph
            color="text.primary"
            sx={{ fontSize: "1.05rem", lineHeight: 1.8 }}
          >
            The platform is designed for researchers and engineers who collect
            sensor-based field data and want to make it more visible and
            reusable. It addresses the engineering sciences community across
            universities, universities of applied sciences, non-university
            research institutions, and related research environments. It is
            particularly suited for:
          </Typography>

          <List>
            {[
              "Researchers conducting sensor-based field experiments",
              "Engineering and Cyber-Physical Systems research projects",
              "Automotive, robotics, mobility, and environmental research groups",
            ].map((item, i) => (
              <ListItem key={i}>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{
                    variant: "body1",
                    fontSize: "1.05rem",
                  }}
                />
              </ListItem>
            ))}
          </List>
        </FaqItem>

        <FaqItem num="03" question="Is the platform a data repository?">
          <Typography
            variant="body1"
            paragraph
            color="text.primary"
            sx={{ fontSize: "1.05rem", lineHeight: 1.8 }}
          >
            No. The FDP does not primarily store large raw datasets. Instead, it
            acts as a discovery and exploration layer where experiments are
            described through structured metadata. Raw data can remain stored in
            institutional repositories or project storage systems.
          </Typography>
        </FaqItem>

        <FaqItem
          num="04"
          question="Why should I publish my dataset on the platform?"
        >
          <Typography
            variant="body1"
            paragraph
            color="text.primary"
            sx={{ fontSize: "1.05rem", lineHeight: 1.8 }}
          >
            Publishing a structured description of your experiment and dataset
            makes it discoverable to other researchers. By documenting metadata
            such as sensing setup and experimental conditions, others can
            identify relevant datasets and build upon the collected data. This
            helps increase the visibility and scientific value of field
            experiments.
          </Typography>
        </FaqItem>

        <FaqItem num="05" question="Where is the platform heading?">
          <Typography
            variant="body1"
            paragraph
            color="text.primary"
            sx={{ fontSize: "1.05rem", lineHeight: 1.8 }}
          >
            The platform is evolving toward a Data Mesh–architecture. In this
            model, research domains will maintain ownership of their datasets
            while a federated structure enables discoverability. The platform
            will gradually incorporate the Data Mesh principles:
          </Typography>
          <List>
            {[
              "Domain-oriented data ownership — research groups manage and publish their own datasets",
              "Data as a product — datasets are treated as structured and documented research assets",
              "Federated computational governance — shared standards ensure interoperability and cross-domain discoverability",
            ].map((item, i) => (
              <ListItem key={i}>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{
                    variant: "body1",
                    fontSize: "1.05rem",
                  }}
                />
              </ListItem>
            ))}
          </List>
        </FaqItem>
      </Box>

      <Divider sx={{ my: 5 }} />

      {/* ── 4. Add Dataset — Variant B with full text ── */}
      <SectionTitle>Add Your Dataset</SectionTitle>
      <Card sx={{ boxShadow: 3, overflow: "hidden" }}>
        {/* dark header */}
        <Box
          sx={{
            background: "#11192A",
            px: 5,
            py: 3,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -80,
              right: -80,
              width: 300,
              height: 300,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(25,118,210,0.20) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Email sx={{ fontSize: 48, color: "#fff" }} />
            <Typography variant="h3" color="#fff" fontWeight="bold">
              Want to publish your field data?
            </Typography>
          </Box>
          <Typography
            variant="body1"
            paragraph
            sx={{
              color: "rgba(255,255,255,0.92)",
              fontSize: "1.1rem",
              lineHeight: 1.9,
            }}
          >
            If you conduct sensor-based field experiments and want to make your
            data discoverable and reusable on the platform, simply send us an
            email.
          </Typography>
        </Box>

        <CardContent sx={{ px: 5, py: 4 }}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: "1.05rem", mb: 3 }}
          >
            Please mention in your email:
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {bulletPoints.map(({ label, detail }, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  color="#11192A"
                  sx={{ fontSize: "1.05rem" }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.95rem", mt: 0.25 }}
                >
                  {detail}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ mb: 3 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* ← Replace with your actual address */}
            <Grid container spacing={3}>
              {[
                {
                  name: "Rayen Hamlaoui",
                  email: "hamlaoui@ipeg.uni-hannover.de",
                },
                {
                  name: "Mario Moser",
                  email: "mario.moser@wzl-iqs.rwth-aachen.de",
                },
              ].map((contact, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Card sx={{ boxShadow: 1 }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="#11192A"
                      >
                        {contact.name}
                      </Typography>

                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {contact.email}
                      </Typography>

                      <Button
                        variant="contained"
                        startIcon={<Email />}
                        href={`mailto:${contact.email}?subject=Field Database Platform Dataset Submission`}
                        sx={{
                          backgroundColor: "#11192A",
                          "&:hover": { backgroundColor: "#1e2a40" },
                        }}
                      >
                        Contact
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LandingPage;
